import { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Grid,
  Card,
  CardContent,
  Divider,
  useMediaQuery,
  useTheme,
  alpha,
  Chip,
  Stack
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import BusinessIcon from "@mui/icons-material/Business";
import EmailIcon from "@mui/icons-material/Email";
import HomeIcon from "@mui/icons-material/Home";
import DescriptionIcon from '@mui/icons-material/Description';

import { getCompanies, addCompany, updateCompany, deleteCompany } from "../Services/companyService";

// Define the Company type
type Company = {
  id: number;
  name: string;
  description: string;
  contactEmail: string;
};

const ManageCompanies = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [open, setOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [companyData, setCompanyData] = useState<Company>({
    id: 0,
    name: "",
    description: "",
    contactEmail: "",
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const data = await getCompanies();
      setCompanies(data);
    } catch (error) {
      console.error("Failed to fetch companies:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (company: Company | null = null) => {
    setEditingCompany(company);
    setCompanyData(
      company ? { ...company } : { id: 0, name: "", description: "", contactEmail: "" }
    );
    setErrors({});
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingCompany(null);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setCompanyData((prev) => ({ ...prev, [name]: value }));
    // Clear error for this field when user types
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    let tempErrors: { [key: string]: string } = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!companyData.name) tempErrors.name = "Company name is required";
    if (!companyData.description) tempErrors.address = "Address is required";
    
    if (!companyData.contactEmail) {
      tempErrors.contactEmail = "Email is required";
    } else if (!emailRegex.test(companyData.contactEmail)) {
      tempErrors.contactEmail = "Please enter a valid email address";
    }

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSave = async () => {
    const isValid = validateForm();
    if (!isValid) return;
    
    try {
      if (editingCompany) {
        await updateCompany(editingCompany.id, companyData);
      } else {
        await addCompany(companyData);
      }
      fetchCompanies();
      handleClose();
    } catch (error) {
      console.error("Failed to save company:", error);
      // Could add error handling UI here
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm("Are you sure you want to delete this company?")) {
      try {
        await deleteCompany(id);
        fetchCompanies();
      } catch (error) {
        console.error("Failed to delete company:", error);
      }
    }
  };

  // Render table view for desktop/tablet
  const renderTable = () => (
    <TableContainer component={Paper} elevation={2} sx={{ overflow: 'auto' }}>
      <Table>
        <TableHead>
          <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.1) }}>
            <TableCell sx={{ fontWeight: "bold" }}>ID</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <BusinessIcon color="primary" fontSize="small" />
                  <Typography fontWeight="medium">Name</Typography>
                </Stack>
            </TableCell>
            {!isTablet && (
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <HomeIcon fontSize="small" color="action" />
                      <Typography>Description</Typography>
                    </Stack>
                  </TableCell>
                )}
              <TableCell>
                  <Stack direction="row" alignItems="center" spacing={0}>
                    <EmailIcon fontSize="small" color="action" />
                    <Typography>Email</Typography>
                  </Stack>
                </TableCell>
            <TableCell sx={{ fontWeight: "bold", width: '120px' }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {companies.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                <Typography variant="body1" color="textSecondary">
                  No companies found. Click "Add Company" to create one.
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            companies.map((company: Company) => (
              <TableRow 
                key={company.id}
                sx={{  
                  '&:last-child td, &:last-child th': { border: 0 },
                  '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.05) }
                }}
              >
                <TableCell>{company.id}</TableCell>
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={0}>
                    <Typography fontWeight="medium">{company.name}</Typography>
                  </Stack>
                </TableCell>
                {!isTablet && (
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={0}>
                      <Typography>{company.description}</Typography>
                    </Stack>
                  </TableCell>
                )}
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={0}>
                    <Typography>{company.contactEmail}</Typography>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Tooltip title="Edit Company">
                    <IconButton 
                      color="primary" 
                      onClick={() => handleOpen(company)}
                      size="small"
                      sx={{ mr: 1 }}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete Company">
                    <IconButton 
                      color="error" 
                      onClick={() => handleDelete(company.id)}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // Render card view for mobile
  const renderCards = () => (
    <Grid container spacing={2} sx={{ mt: 2 }}>
      {companies.length === 0 ? (
        <Grid item xs={12}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No companies found. Click "Add Company" to create one.
            </Typography>
          </Paper>
        </Grid>
      ) : (
        companies.map((company) => (
          <Grid item xs={12} key={company.id}>
            <Card elevation={2} sx={{ overflow: 'visible' }}>
              <CardContent>
                <Grid container alignItems="center" spacing={1}>
                  <Grid item>
                    <BusinessIcon color="primary" />
                  </Grid>
                  <Grid item xs>
                    <Typography variant="h6" component="div">
                      {company.name}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Chip label={`ID: ${company.id}`} size="small" />
                  </Grid>
                </Grid>
                
                <Divider sx={{ my: 1.5 }} />
                
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                  <HomeIcon fontSize="small" color="action" />
                  <Typography variant="body2">{company.description}</Typography>
                </Stack>
                
                <Stack direction="row" alignItems="center" spacing={1}>
                  <EmailIcon fontSize="small" color="action" />
                  <Typography variant="body2">{company.contactEmail}</Typography>
                </Stack>
                
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    startIcon={<EditIcon />}
                    size="small"
                    onClick={() => handleOpen(company)}
                    sx={{ mr: 1 }}
                  >
                    Edit
                  </Button>
                  <Button
                    startIcon={<DeleteIcon />}
                    color="error"
                    size="small"
                    onClick={() => handleDelete(company.id)}
                  >
                    Delete
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))
      )}
    </Grid>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
          Manage Companies
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<AddCircleIcon />}
          onClick={() => handleOpen()}
          size={isMobile ? "small" : "medium"}
        >
          {isMobile ? "Add" : "Add Company"}
        </Button>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        isMobile ? renderCards() : renderTable()
      )}

      {/* Add/Edit Company Dialog */}
      <Dialog 
        open={open} 
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        fullScreen={isMobile}
        PaperProps={{
          elevation: 2,
          sx: { borderRadius: 1 }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: `1px solid ${theme.palette.divider}`,
          py: 2
        }}>
          <Typography variant="h6">
            {editingCompany ? "Edit Company" : "Add Company"}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ mt: 2,mb:2, pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                name="name"
                label="Company Name"
                margin="dense"
                fullWidth
                value={companyData.name}
                onChange={handleChange}
                error={!!errors.name}
                helperText={errors.name || ""}
                InputProps={{ 
                  startAdornment: <BusinessIcon color="action" sx={{ mr: 2 }} /> 
                }}
                variant="outlined"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ maxHeight: 200, overflow: "auto", width: "100%" }}>
                <TextField
                  name="description"
                  label="Company Description"
                  fullWidth
                  value={companyData.description}
                  onChange={handleChange}
                  error={!!errors.description}
                  helperText={errors.description || ""}
                  InputProps={{ 
                    startAdornment: <DescriptionIcon color="action" sx={{ mr: 1 }} /> 
                  }}
                  variant="outlined"
                  multiline
                  rows={4}  // Starts with 4 rows
                  maxRows={10} // Expands up to 10 rows
                  margin="normal" // Adds spacing
                  required
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="contactEmail"
                label="Email Address"
                fullWidth
                value={companyData.contactEmail}
                onChange={handleChange}
                error={!!errors.contactEmail}
                helperText={errors.contactEmail || ""}
                InputProps={{ 
                  startAdornment: <EmailIcon color="action" sx={{ mr: 1 }} /> 
                }}
                variant="outlined"
                type="email"
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ 
          px: 3, 
          py: 2, 
          borderTop: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1
        }}>
          <Button 
            onClick={handleClose} 
            color="inherit"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            color="primary"
            variant="contained"
            disableElevation
          >
            {editingCompany ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageCompanies;